import React from "react";
import { observer } from "mobx-react";
import { EditionCreateStore } from "../../store";
import { EditionCreateForm } from "./EditionCreateForm";
import { ErrorDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { MusicalWorkMiniForm } from "../../../musicalWork/components/view/MusicalWorkMiniForm";
import { EditionContentType } from "../../../../api/graphql";

export type EditionCreateProps = {
  editionCreateStore: EditionCreateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const EditionCreate: React.FC<EditionCreateProps> = observer(
  ({ editionCreateStore, showToolbox = true, showSaveFooter }) => {
    return (
      <>
        <SavingOverlay open={editionCreateStore.isSaving} />
        <EditionCreateForm
          editionCreateStore={editionCreateStore}
          nameError={editionCreateStore.errors.name?.message}
          onNameChange={(value: string) => {
            editionCreateStore.setNameValue(value);
          }}
          name={editionCreateStore.entityValues.name}
          editionTypeError={editionCreateStore.errors.editionType?.message}
          onEditionTypeChange={(value) => {
            editionCreateStore.setEditionTypeValue(value);
          }}
          editionTypeSelectedValue={
            editionCreateStore.entityValues.showEditionType
          }
          onEditionTypeSelected={(value) => {
            editionCreateStore.setSelectedEditionType(value);
          }}
          editionTypeOptions={editionCreateStore.getEditionTypeOptions()}
          contentTypeError={editionCreateStore.errors.contentType?.message}
          contentTypeOptions={editionCreateStore.getContentTypeOptions()}
          onContentTypeChange={(value: EditionContentType) => {
            editionCreateStore.setContentTypeValue(value);
          }}
          contentType={editionCreateStore.entityValues.contentType}
          publicationLinkError={
            editionCreateStore.errors.publicationLink?.message
          }
          onPublicationLinkChange={(value: string) => {
            editionCreateStore.setPublicationLinkValue(value);
          }}
          publicationLink={editionCreateStore.entityValues.publicationLink}
          priceError={editionCreateStore.errors.price?.message}
          onPriceChange={(value: number) => {
            editionCreateStore.setPriceValue(value);
          }}
          price={editionCreateStore.entityValues.price}
          worksError={editionCreateStore.errors.works?.message}
          onWorksChange={(value) => {
            editionCreateStore.setWorksValue(value);
          }}
          worksSelectedValues={editionCreateStore.entityValues.showWorks}
          onWorksSelected={(value) => {
            editionCreateStore.setSelectedWorks(value);
          }}
          worksOptions={editionCreateStore.getWorksOptions()}
          worksOptionsMap={(option: any, backgroundColor: string) => (
            <MusicalWorkMiniForm
              data={option}
              backgroundColor={backgroundColor}
              visibleFields={["title", "authors"]}
            />
          )}
          worksMiniFormMode="tooltip"
          notesError={editionCreateStore.errors.notes?.message}
          onNotesChange={(value: string) => {
            editionCreateStore.setNotesValue(value);
          }}
          notes={editionCreateStore.entityValues.notes}
          contractObservationError={
            editionCreateStore.errors.contractObservation?.message
          }
          onContractObservationChange={(value: string) => {
            editionCreateStore.setContractObservationValue(value);
          }}
          contractObservation={
            editionCreateStore.entityValues.contractObservation
          }
          userAttachmentEditionCreateStore={
            editionCreateStore.userAttachmentsCreateStore
          }
          userAttachmentsVisibleColumns={
            editionCreateStore.userAttachmentsVisibleColumns
          }
          userAttachmentsOnRestore={(id: string) =>
            editionCreateStore.userAttachmentsRestore(id)
          }
          userAttachmentsOnCommitChanges={(changes) =>
            editionCreateStore.userAttachmentsCommitChanges(changes)
          }
          userAttachmentsValidateChanges={(changes) =>
            editionCreateStore.userAttachmentsValidateChanges(changes)
          }
          userAttachmentsRows={
            editionCreateStore.entityValues.showUserAttachments
          }
          userAttachmentsError={
            editionCreateStore.errors.userAttachments?.message
          }
          dummyCRUDRestoredObservable={
            editionCreateStore.dummyCRUDRestoredObservable
          }
          showToolbox={showToolbox}
          showSaveFooter={showSaveFooter}
          saving={editionCreateStore.isProcessing}
          onSave={() => editionCreateStore.saveNewEdition()}
          onCancel={() => editionCreateStore.cancelNewEdition()}
          viewFormWorks={(id: string) => editionCreateStore.viewFormWorks(id)}
        />
        <ErrorDialog
          open={editionCreateStore.errorDialogOpened}
          title={editionCreateStore.errorTitle}
          description={editionCreateStore.errorDescription}
          onClose={() => editionCreateStore.closeErrorDialog()}
        />
      </>
    );
  }
);
